import React from 'react';
import type { AppBoxSkin, Options } from '../../components/app-box';
import { Grid } from '../../components/grid';
import { AppBox } from '../../components/app-box';
import { withEssentialsContext } from '../../contexts/essentials-context';
import type {
  ExternalComponentProps,
  ComponentsWithSiteContextProps,
} from '../types';
import { VisibilityDetector } from '../visibility-detector';
import { v4 as generateUuidV4 } from 'uuid';
import { withSiteContext } from '../../contexts/site-context';
import type { BaseApp } from '../../types';
import { AppGridSkeleton } from './app-grid-skeleton';

export interface AppGridProps
  extends ExternalComponentProps,
    ComponentsWithSiteContextProps {
  dataHook?: string;
  apps: BaseApp[];
  columns: 1 | 2 | 3 | 4;
  minColumns?: 1 | 2 | 3 | 4;
  rows?: number;
  itemMinWidth?: string;
  options?: Options;
  onAppClick?(app: BaseApp, appIndex: number, impressionId: string): void;
  onAppLoad?(app: BaseApp, appIndex: number, impressionId: string): void;
  getHref?(appSlug: string, appIndex: number): string | undefined;
  showBorder?: boolean;
  finalColumnsCount?: (finalColumns: number) => void;
  skin?: AppBoxSkin;
  loading?: {
    isLoading: boolean;
    boxCount: number;
  };
}

function AppGridComponent({
  dataHook,
  apps,
  columns,
  minColumns,
  rows,
  itemMinWidth,
  loading,
  onAppClick,
  options,
  onAppLoad,
  getHref,
  showBorder,
  finalColumnsCount,
  languageCode = 'en',
  skin = 'default',
}: AppGridProps) {
  if (loading?.isLoading) {
    return (
      <AppGridSkeleton
        boxCount={loading.boxCount}
        columns={columns}
        minColumns={minColumns}
        rows={rows}
        skin={skin}
        dataHook="app-grid-loader"
      />
    );
  }

  return (
    <Grid
      dataHook={dataHook}
      columns={columns}
      rows={rows}
      minColumns={minColumns}
      itemMinWidth={itemMinWidth}
      finalColumnsCount={finalColumnsCount}
      skin={skin}
    >
      {apps.map((app, index) => {
        const impressionId = generateUuidV4();
        return (
          <VisibilityDetector
            onFirstVisible={() => {
              app && onAppLoad?.(app, index, impressionId);
            }}
            key={index}
          >
            <AppBox
              appData={app}
              options={{
                showBadgesVertical: columns !== 1,
                showBorder,
                ...options,
              }}
              onClick={() => onAppClick?.(app, index, impressionId)}
              href={getHref?.(app.slug!, index)}
              languageCode={languageCode}
              skin={skin}
            />
          </VisibilityDetector>
        );
      })}
    </Grid>
  );
}

export const AppGrid = withSiteContext(withEssentialsContext(AppGridComponent));
